import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import Checkbox from 'components/checkbox';
import Collapsible from 'components/collapsible';
import Divider from 'components/divider';
import ListCell from 'components/list/list-cell';
import LoadingSpinner from 'components/loading-spinner';
import NewList from 'components/new-list';
import NewListRow from 'components/new-list-row';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { Info, TimerReset } from 'lucide-react';
import Mui from 'materials';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { isNullOrUndefined } from 'utils/empty-or-null';
import {
  getSeaCloudCages,
  getSeaCloudGenerations,
  putR2Calibration,
} from 'views/bluviu-nutrition/features/calibrations/api/r2-calibration';
import { useCalibrationState } from 'views/bluviu-nutrition/features/calibrations/context/calibration-context';
import { R2Calibration } from 'views/bluviu-nutrition/features/calibrations/types/r2-calibration.type';
import { UpdateR2CalibrationInputsRequest } from 'views/bluviu-nutrition/features/calibrations/types/update-r2-calibration-inputs-request.type';
import { CalibrationsParams } from '../../../types/calibrations-params.type';

const PAGE_NAME_ID = `bluViuNutrition.calibrations.editCalibration.inputs.multi`;

const R2CalibrationInputs: React.FC<{}> = ({}) => {
  const theme = useTheme();
  const snackbarDispatch = useSnackbarDispatch();
  const params = useParams<CalibrationsParams>();
  const queryClient = useQueryClient();
  const intl = useIntl();

  const {
    r2CalibrationQuery: { data: r2Calibration, isLoading },
  } = useCalibrationState();

  const [generation, setGeneration] = useState<string>('');
  const [selectedFishGroups, setSelectedFishGroups] = useState<string[]>([]);

  const defaultSelectedFishGroups: string[] = useMemo(() => {
    if (!r2Calibration) {
      return [];
    }
    return r2Calibration.cages.map((cage) => cage.fishGroupId);
  }, [r2Calibration]);

  const defaultGeneration = useMemo(() => {
    if (!r2Calibration) return '';
    return String(r2Calibration.generation);
  }, [r2Calibration]);

  useEffect(() => {
    setSelectedFishGroups(defaultSelectedFishGroups);
    setGeneration(defaultGeneration);
  }, [defaultSelectedFishGroups, defaultGeneration]);

  const { data: seaCloudGenerations, isLoading: isGenerationsLoading } = useGet(
    ['getSeaCloudGenerations', params.globalSiteId],
    ({ queryKey }) => getSeaCloudGenerations(String(queryKey[1])),
    { enabled: !!r2Calibration }
  );

  const { data: cages, isLoading: isCagesLoading } = useGet(
    ['getSeaCloudCages', params.globalSiteId, generation],
    ({ queryKey }) => getSeaCloudCages(String(queryKey[1]), Number(queryKey[2])),
    {
      enabled: !isNullOrUndefined(generation) && Number(generation) !== 0,
    }
  );

  const currentYear = useMemo(() => {
    const selectedSet = new Set(selectedFishGroups);
    const ids = cages?.map((c) => c.fishGroupId) ?? [];
    const selected = ids.filter((id) => selectedSet.has(id));
    return {
      ids,
      selected,
      allSelected: ids.length > 0 && selected.length === ids.length,
      someSelected: selected.length > 0,
    };
  }, [cages, selectedFishGroups]);

  const isEqual = useMemo(() => {
    if (String(r2Calibration?.generation) !== generation) return false;
    if (!r2Calibration) return false;
    if (r2Calibration.cages.length !== currentYear.selected.length) return false;
    const selectedSet = new Set(currentYear.selected);
    return r2Calibration.cages.every((cage) => selectedSet.has(cage.fishGroupId));
  }, [r2Calibration, generation, currentYear.selected]);

  const updateR2CalibrationInputs = usePost<R2Calibration, UpdateR2CalibrationInputsRequest>(
    (request) => putR2Calibration(params.globalSiteId, params.calibrationId, request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: 'Inputs saved!' },
      });
      queryClient.invalidateQueries({
        queryKey: ['getR2Calibration', params.globalSiteId, params.calibrationId]
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  const handleSelectFishGroup = (value: string, checked: boolean) => {
    setSelectedFishGroups(checked ? selectedFishGroups.concat([value]) : selectedFishGroups.filter((s) => s !== value));
  };

  const handleSubmit = () => {
    updateR2CalibrationInputs.mutate({
      generation: Number(generation),
      fishGroups: currentYear.selected,
    });
  };

  const handleSelectAllFishGroups = useCallback(() => {
    if (!currentYear.ids.length) return;
    const idsSet = new Set(currentYear.ids);
    if (currentYear.allSelected) {
      setSelectedFishGroups(selectedFishGroups.filter((id) => !idsSet.has(id)));
    } else {
      const selectedSet = new Set(selectedFishGroups);
      setSelectedFishGroups(selectedFishGroups.concat(currentYear.ids.filter((id) => !selectedSet.has(id))));
    }
  }, [currentYear, selectedFishGroups]);

  const reset = useCallback(() => {
    setSelectedFishGroups(defaultSelectedFishGroups);
    setGeneration(defaultGeneration);
  }, [defaultGeneration, defaultSelectedFishGroups, r2Calibration]);

  if (isLoading) return <LoadingSpinner />;

  if (!r2Calibration) return; // is multi cage calibration needed here? how do we populate the list if values exist already

  return (
    <Collapsible label={'Inputs'} open>
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Box display={'flex'} flexDirection={'column'} gap={5}>
          <Box display={'flex'} justifyContent={'flex-end'}>
            <Typography color={theme.palette.darkBlue.o75}>
              <FormattedMessage id={`${PAGE_NAME_ID}.information`} />
            </Typography>
          </Box>
          <Select
            label={intl.formatMessage({ id: `${PAGE_NAME_ID}.selectYear` })}
            dark
            fullWidth
            isLoading={isGenerationsLoading}
            value={generation}
            noResults={{
              show: !seaCloudGenerations?.length,
              text: intl.formatMessage({ id: `general.noResults` }),
            }}
            onChange={(e) => setGeneration(e.target.value as string)}>
            {seaCloudGenerations?.map((generation) => (
              <RadioSelectItem key={generation} value={generation}>
                {generation}
              </RadioSelectItem>
            ))}
          </Select>
          {Number(generation) !== 0 && (
            <Box display={'flex'} flexDirection={'column'} gap={5}>
              <Divider dark flexItem orientation="horizontal" sx={{ borderColor: theme.palette.darkBlue.o25 }} />
              <Box display={'flex'} justifyContent={'flex-end'}>
                <Typography color={theme.palette.darkBlue.o75}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.cages.information`} />
                </Typography>
              </Box>
              <NewList
                isLoading={isCagesLoading}
                noResult={{
                  show: !cages?.length,
                  text: intl.formatMessage({ id: `general.noResults` }),
                }}>
                <Mui.TableHead>
                  <Mui.TableRow>
                    <Mui.TableCell
                      sx={{
                        paddingBottom: '1%',
                      }}>
                      <Checkbox
                        iconSize={18}
                        dark
                        checked={currentYear.allSelected}
                        indeterminate={currentYear.someSelected && !currentYear.allSelected}
                        onChange={() => handleSelectAllFishGroups()}
                      />
                    </Mui.TableCell>
                    <Mui.TableCell>
                      <Typography fontWeight={'bold'} fontSize={12}>
                        <FormattedMessage id={`${PAGE_NAME_ID}.cages.list.fishGroup`} />
                      </Typography>
                    </Mui.TableCell>
                    <Mui.TableCell>
                      <Typography fontWeight={'bold'} fontSize={12}>
                        <FormattedMessage id={`${PAGE_NAME_ID}.cages.list.stockingDate`} />
                      </Typography>
                    </Mui.TableCell>
                    <Mui.TableCell align="right">
                      <Typography fontWeight={'bold'} fontSize={12}>
                        <FormattedMessage id={`${PAGE_NAME_ID}.cages.list.startWeight`} />
                      </Typography>
                    </Mui.TableCell>
                    <Mui.TableCell align="right">
                      <Typography fontWeight={'bold'} fontSize={12}>
                        <FormattedMessage id={`${PAGE_NAME_ID}.cages.list.bFCR`} />
                      </Typography>
                    </Mui.TableCell>
                    <Mui.TableCell>
                      <Typography fontWeight={'bold'} fontSize={12}>
                        <FormattedMessage id={`${PAGE_NAME_ID}.cages.list.harvestDate`} />
                      </Typography>
                    </Mui.TableCell>
                    <Mui.TableCell align="right">
                      <Typography fontWeight={'bold'} fontSize={12}>
                        <FormattedMessage id={`${PAGE_NAME_ID}.cages.list.avgHarvestMW`} />
                      </Typography>
                    </Mui.TableCell>
                    <Mui.TableCell align="right">
                      <Typography fontWeight={'bold'} fontSize={12}>
                        <FormattedMessage id={`${PAGE_NAME_ID}.cages.list.egi`} />
                      </Typography>
                    </Mui.TableCell>
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {cages?.map((cage, idx) => (
                    <NewListRow key={idx}>
                      <ListCell>
                        <Checkbox
                          tabIndex={-1}
                          dark
                          hiddenLabel
                          checked={selectedFishGroups.includes(cage.fishGroupId)}
                          onChange={(_, checked) => handleSelectFishGroup(cage.fishGroupId, checked)}
                        />
                      </ListCell>
                      <ListCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {cage.fishGroupName}
                          <Mui.Tooltip
                            title={intl.formatMessage({ id: `${PAGE_NAME_ID}.cages.list.cageTooltip` }, { unitName: cage.unitName })}>
                            <Info size={14} />
                          </Mui.Tooltip>
                        </Box>
                      </ListCell>
                      <ListCell align={'left'}>
                        <FormattedDate value={cage.stockingDate} />
                      </ListCell>
                      <ListCell align={'right'}>
                        <FormattedNumber value={cage.stockingWeight} /> g
                      </ListCell>
                      <ListCell align={'right'}>
                        <FormattedNumber value={cage.bFcr} />
                      </ListCell>
                      <ListCell>
                        <FormattedDate value={cage.harvestDate} />
                      </ListCell>
                      <ListCell align={'right'}>
                        <FormattedNumber value={cage.harvestWeight} /> g
                      </ListCell>
                      <ListCell align={'right'}>
                        <FormattedNumber value={cage.egi} />
                      </ListCell>
                    </NewListRow>
                  ))}
                </Mui.TableBody>
              </NewList>
              <Box display={'flex'} justifyContent={'space-between'}>
                <Box display={'flex'} alignItems={'center'}>
                  <Mui.Tooltip title={'Reset feed program'}>
                    <Mui.IconButton disabled={isEqual} onClick={() => reset()}>
                      <TimerReset size={22} />
                    </Mui.IconButton>
                  </Mui.Tooltip>
                </Box>
                <Button
                  onClick={() => handleSubmit()}
                  disabled={updateR2CalibrationInputs.isPending || isEqual}
                  startIcon={updateR2CalibrationInputs.isPending && <CircularProgress color="inherit" size={18} />}>
                  <FormattedMessage id={`general.save`} />
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Collapsible>
  );
};

export default React.memo(R2CalibrationInputs);
